<template>
    <v-dialog v-model="dialog" persistent scrollable max-width="620">
        <template v-slot:activator="{ on }">
            <v-list-item v-on="on" @click.stop.prevent>
                <v-list-item-icon>
                    <v-icon>mdi-plus</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Assign roles</v-list-item-title>
            </v-list-item>
        </template>
        <v-card flat>
            <v-card-title class="flex-nowrap align-start secondary--text">
                <div>
                    <div>Assign roles</div>
                    <div class="d-flex align-center text-subtitle-2 font-weight-regular mt-1">
                        <v-icon x-small color="primary" class="mr-2">mdi-circle</v-icon>
                        <span class="mr-1">Assignee:</span>
                        <span class="font-weight-medium">{{ assignee }}</span>
                    </div>
                </div>
                <v-spacer />
                <v-btn icon :disabled="assigningRole" @click="clearAndCloseDialog"><v-icon>clear</v-icon></v-btn>
            </v-card-title>

            <v-tabs v-model="assignmentTab" grow background-color="grey lighten-5">
                <v-tab :disabled="assigningRole">
                    <v-icon left small>mdi-account-multiple-outline</v-icon>
                    Select roles
                </v-tab>
                <v-tab :disabled="disableReviewTab">
                    <v-icon left small>mdi-clipboard-check-outline</v-icon>
                    Results
                </v-tab>
            </v-tabs>

            <v-card-text class="pa-6">
                <v-tabs-items v-model="assignmentTab">
                    <v-tab-item>
                        <v-text-field
                            v-model="targetSearch"
                            placeholder="Search space and instances..."
                            prepend-inner-icon="mdi-magnify"
                            background-color="grey lighten-5"
                            solo
                            flat
                            dense
                            clearable
                            hide-details
                            class="mb-6" />

                        <section v-if="showSpaceRole" class="mb-6">
                            <div class="d-flex align-center mb-2">
                                <div class="text-overline secondary--text">Space role</div>
                                <v-spacer />
                                <span class="text-caption">1 parent space</span>
                            </div>
                            <v-card outlined :class="{ 'selected-border': spaceRoleBox }">
                                <v-card-text class="d-flex align-center py-4">
                                    <v-checkbox v-model="spaceRoleBox" :disabled="assigningRole" hide-details class="ma-0 pa-0 mr-4" />
                                    <v-icon :color="spaceRoleBox ? 'primary' : 'secondary'" class="mr-3">
                                        {{ getSpaceRole(roleTypes.SPACE_ADMIN).icon }}
                                    </v-icon>
                                    <div>
                                        <div class="text-subtitle-1 font-weight-medium" :class="{ 'primary--text': spaceRoleBox }">
                                            {{ getSpaceRole(roleTypes.SPACE_ADMIN).label }}
                                        </div>
                                        <div class="text-body-2 mt-1" :class="{ 'primary--text': spaceRoleBox }">{{ spaceName }}</div>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </section>

                        <section>
                            <div class="d-flex align-center mb-2">
                                <div class="text-overline secondary--text">Instance roles</div>
                                <v-spacer />
                                <v-chip small color="grey lighten-4" class="secondary--text">{{ filteredInstanceList.length }} instances</v-chip>
                            </div>

                            <div v-if="filteredInstanceList.length">
                                <v-card
                                    v-for="instance in filteredInstanceList"
                                    :key="instance.iid"
                                    outlined
                                    class="mb-3"
                                    :class="{
                                        'selected-border': hasPendingInstanceChange(instance) && !instance.revokeRole,
                                        'revoke-border': instance.revokeRole
                                    }">
                                    <v-card-text class="py-4">
                                        <div class="d-flex align-center">
                                            <v-icon
                                                :color="instance.revokeRole ? 'error' : hasPendingInstanceChange(instance) ? 'primary' : 'secondary'"
                                                class="mr-3">
                                                {{ getInstanceTypeIcon(instance.instance_name) }}
                                            </v-icon>
                                            <span
                                                class="text-subtitle-1 font-weight-medium"
                                                :class="{
                                                    'primary--text': hasPendingInstanceChange(instance) && !instance.revokeRole,
                                                    'error--text': instance.revokeRole
                                                }">
                                                {{ instance.instance_name }}
                                            </span>
                                            <v-spacer />
                                            <v-btn
                                                v-if="instance.currentRole || instance.selectedRole"
                                                text
                                                small
                                                :color="instance.currentRole && !instance.revokeRole ? 'error' : 'secondary'"
                                                :disabled="assigningRole"
                                                @click="toggleInstanceRoleRevocation(instance)">
                                                {{ !instance.currentRole || instance.revokeRole ? 'Undo' : 'Revoke' }}
                                            </v-btn>
                                        </div>
                                        <v-radio-group
                                            v-model="instance.selectedRole"
                                            :disabled="assigningRole"
                                            hide-details
                                            class="mt-3 pt-0"
                                            @change="instance.revokeRole = false">
                                            <v-row dense>
                                                <v-col v-for="availableRole in availableInstanceRoles" :key="availableRole" cols="12" sm="4">
                                                    <v-card
                                                        outlined
                                                        :disabled="assigningRole"
                                                        class="d-flex align-center justify-center px-3 py-2 overflow-hidden"
                                                        :class="{ 'blue lighten-5 primary--text': instance.selectedRole === availableRole }"
                                                        @click="selectInstanceRole(instance, availableRole)">
                                                        <v-radio :value="availableRole" class="ma-0 pa-0">
                                                            <template v-slot:label>
                                                                <span :class="{ 'primary--text font-weight-medium': instance.selectedRole === availableRole }">
                                                                    {{ getInstanceRole(availableRole).label }}
                                                                </span>
                                                            </template>
                                                        </v-radio>
                                                    </v-card>
                                                </v-col>
                                            </v-row>
                                        </v-radio-group>
                                    </v-card-text>
                                </v-card>
                            </div>
                            <v-alert v-else text type="info" class="mb-0">No matching roles found.</v-alert>
                        </section>
                    </v-tab-item>

                    <v-tab-item>
                        <div v-if="assigningRole" class="text-center py-8">
                            <v-progress-circular indeterminate color="primary" class="mb-3" />
                            <div>Applying role changes...</div>
                        </div>
                        <v-list v-else-if="roleAssignmentOutcome.length" two-line class="pa-0">
                            <v-list-item v-for="(outcome, index) in roleAssignmentOutcome" :key="index" class="px-0">
                                <v-list-item-icon>
                                    <v-icon :color="outcome.outcome === 'success' ? 'success' : 'error'">
                                        {{ outcome.outcome === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                    </v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>{{ outcome.target }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ outcome.role }}</v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                    <v-chip :color="outcome.outcome === 'success' ? 'success' : 'error'" small outlined>
                                        {{ outcome.outcome === 'success' ? outcome.action : 'Failed' }}
                                    </v-chip>
                                </v-list-item-action>
                            </v-list-item>
                        </v-list>
                    </v-tab-item>
                </v-tabs-items>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <span v-if="assignmentTab === assignmentTabOptions.SELECT_ROLES_TAB" class="text-caption secondary--text ml-2">
                    {{ pendingChangesLength }} {{ pendingChangesLength === 1 ? 'change' : 'changes' }} selected
                </span>
                <v-spacer />
                <v-btn v-if="finishedAssignments" color="primary" text @click="clearAndCloseDialog">Done</v-btn>
                <v-btn v-else color="primary" text :loading="assigningRole" :disabled="!activateAssignButton" @click="applyRoleChanges">Apply changes</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script>
import { enumsData } from '@/mixins/enums'
import { getInstanceTypeIcon, instanceRole as getInstanceRole, spaceRole as getSpaceRole } from '@/utils'

export default {
    name: 'AssignRolesDialog',
    mixins: [enumsData],
    props: {
        instanceData: Array,
        assignee: String,
        spaceName: String,
        ucid: String,
        spaceRole: String
    },
    data() {
        return {
            dialog: false,
            instances: [],
            targetSearch: '',
            spaceRoleBox: false,
            assigningRole: false,
            assignmentTab: 0,
            assignmentTabOptions: {
                SELECT_ROLES_TAB: 0,
                REVIEW_AND_ASSIGN_TAB: 1
            },
            roleAssignmentOutcome: [],
            disableReviewTab: true,
            finishedAssignments: false
        }
    },
    methods: {
        getInstanceTypeIcon,
        getInstanceRole,
        getSpaceRole,
        hasPendingInstanceChange(instance) {
            return instance.revokeRole || (instance.selectedRole && instance.selectedRole !== instance.currentRole)
        },
        selectInstanceRole(instance, role) {
            if (!this.assigningRole) {
                instance.selectedRole = role
                instance.revokeRole = false
            }
        },
        toggleInstanceRoleRevocation(instance) {
            if (!instance.currentRole) {
                instance.selectedRole = ''
                return
            }
            instance.revokeRole = !instance.revokeRole
            instance.selectedRole = instance.revokeRole ? '' : instance.currentRole
        },
        applyRoleChanges: function () {
            this.disableReviewTab = false
            this.assignmentTab = this.assignmentTabOptions.REVIEW_AND_ASSIGN_TAB
            this.assigningRole = true
            const assignments = this.instances.filter(
                instance => !instance.revokeRole && instance.selectedRole && instance.selectedRole !== instance.currentRole
            )
            const revocations = this.instances.filter(instance => instance.revokeRole && instance.currentRole)
            const requests = [
                ...(this.spaceRoleBox ? [this.assignSpaceRole()] : []),
                ...assignments.map(instance => this.assignInstanceRole(instance)),
                ...revocations.map(instance => this.revokeInstanceRole(instance))
            ]
            Promise.all(requests).finally(() => {
                this.assigningRole = false
                this.finishedAssignments = true
            })
        },
        resetSelectedSpaceData: function () {
            this.spaceRoleBox = false
        },
        clearAndCloseDialog: function () {
            if (this.roleAssignmentOutcome.length) {
                this.$store.dispatch('spaceStore/fetchSpaceUsers', this.$route.params.sid)
            }
            this.targetSearch = ''
            this.resetSelectedSpaceData()
            this.roleAssignmentOutcome = []
            this.dialog = false
            this.assignmentTab = this.assignmentTabOptions.SELECT_ROLES_TAB
            this.disableReviewTab = true
            this.finishedAssignments = false
            this.$emit('close-menu')
        },
        assignSpaceRole: function () {
            const apiURL = `/spaces/${this.$route.params.sid}/assign_role_for_conn`
            const postBody = {
                role_name: this.roleTypes.SPACE_ADMIN,
                ucid: this.ucid
            }
            return this.$axios
                .post(apiURL, postBody)
                .then(() => {
                    this.roleAssignmentOutcome.push({
                        role: this.getSpaceRole(this.roleTypes.SPACE_ADMIN).label,
                        target: this.spaceName,
                        outcome: 'success',
                        action: 'Assigned'
                    })
                    this.spaceRoleBox = false
                })
                .catch(() => {
                    this.roleAssignmentOutcome.push({
                        role: this.getSpaceRole(this.roleTypes.SPACE_ADMIN).label,
                        target: this.spaceName,
                        outcome: 'failure',
                        action: 'Assigned'
                    })
                })
        },
        assignInstanceRole: function (instance) {
            const roleToAssign = instance.selectedRole
            return this.$axios
                .post(`/instances/${instance.iid}/assign_role_for_conn`, {
                    role_name: roleToAssign,
                    ucid: this.ucid
                })
                .then(() => {
                    this.roleAssignmentOutcome.push({
                        role: this.getInstanceRole(roleToAssign).label,
                        target: instance.instance_name,
                        outcome: 'success',
                        action: 'Assigned'
                    })
                    instance.currentRole = roleToAssign
                })
                .catch(() => {
                    this.roleAssignmentOutcome.push({
                        role: this.getInstanceRole(roleToAssign).label,
                        target: instance.instance_name,
                        outcome: 'failure',
                        action: 'Assigned'
                    })
                })
        },
        revokeInstanceRole: function (instance) {
            const revokedRole = instance.currentRole
            return this.$axios
                .delete(`/instances/${instance.iid}/revoke_role_from_conn/${this.ucid}`)
                .then(() => {
                    this.roleAssignmentOutcome.push({
                        role: this.getInstanceRole(revokedRole).label,
                        target: instance.instance_name,
                        outcome: 'success',
                        action: 'Revoked'
                    })
                    instance.currentRole = ''
                    instance.revokeRole = false
                })
                .catch(() => {
                    this.roleAssignmentOutcome.push({
                        role: this.getInstanceRole(revokedRole).label,
                        target: instance.instance_name,
                        outcome: 'failure',
                        action: 'Revoked'
                    })
                })
        }
    },
    computed: {
        availableInstanceRoles() {
            return [this.roleTypes.INSTANCE_EDITOR, this.roleTypes.INSTANCE_VIEWER, this.roleTypes.INSTANCE_OBSERVER]
        },
        showSpaceRole() {
            const search = (this.targetSearch || '').toLowerCase()
            return this.spaceName && this.spaceRole !== this.roleTypes.SPACE_ADMIN && this.spaceName.toLowerCase().includes(search)
        },
        activateAssignButton() {
            return Boolean(this.pendingChangesLength)
        },
        pendingChangesLength() {
            return this.instances.filter(this.hasPendingInstanceChange).length + Number(this.spaceRoleBox)
        },
        filteredInstanceList() {
            const search = (this.targetSearch || '').toLowerCase()
            if (search) {
                return this.instances.filter(instance => instance.instance_name.toLowerCase().includes(search))
            } else {
                return this.instances
            }
        }
    },
    watch: {
        instanceData: {
            handler(nextVal) {
                this.instances = (nextVal || []).map(instance => ({
                    ...instance,
                    currentRole: instance.selectedRole || '',
                    revokeRole: false
                }))
            },
            immediate: true
        }
    }
}
</script>

<style scoped>
.selected-border {
    border-color: var(--v-primary-base) !important;
}

.revoke-border {
    border-color: var(--v-error-base) !important;
}
</style>
